import {
	useFormContext,
	useController,
	Controller,
	FieldValues,
	FieldPath,
	UseControllerProps,
	get,
	SubmitHandler,
} from "react-hook-form";
import React, { ChangeEvent, FC, useCallback, useEffect, useState, useRef } from "react";
import './css/multiRangeSlider.css';
import { HashLink } from "react-router-hash-link";
import {
	TextField,
	TextFieldProps,
	RadioGroup,
	Radio,
	Autocomplete,
	FormControlLabel,
	FormLabel,
	Grid,
	Tooltip,
	IconButton,
	Switch,
	Button,
	createTheme,
	AppBar,
	Toolbar,
	Typography,
	Box,
	Menu,
	MenuItem,
	Paper,
} from "@mui/material";
import { styled } from "@mui/system";
import {
	bubbleColor,
	dummyFile,
	fontFamily,
	cssStyleOverrides,
	pageBackgroundColor,
	textColor,
	Result
} from "./constants";
/** MUI icons */
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuIcon from "@mui/icons-material/Menu";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningIcon from "@mui/icons-material/Warning";
import Container from "@mui/material/Container";
import LaunchIcon from "@mui/icons-material/Launch";
import { Search } from "@mui/icons-material";

/**
 * NOTE: DO NOT use generic, non-parameterized input components. `Navbar`,
 * `ResetButton`, `SubmitButton`, & `PDBViewer` are
 * exceptions to this and are used as generic components.
 */

/**
 * Generic input parameters present in all custom input components in this
 * file except `Navbar`, `ResetButton`, `SubmitButton`, & `PDBViewer`.
 * **MUST** be parameterized (`T`) according to the form fields inferred
 * from a Zod schema. Must also pass in the parameter `name`, which must
 * match one of the keys from this defined Zod schema object.
 */
interface InputProps<T extends FieldValues> extends UseControllerProps<T> {
	/** Form label to display to the left of the input component. */
	label: string;
	/** Marks labels as green for conditionally-rendered inputs. */
	isChild?: boolean;
	/**
	 * Other JSX elements to add within an input container (i.e.,
	 * children).
	 */
	children?: React.ReactNode;
	/**
	 * Helper text to be displayed in a tooltip component (represented
	 * by the '?' symbol) next to the input component for greater user
	 * accessibility when hovered over.
	 */
	helperText?: React.ReactNode;
	/**
	 * Allows for a component to be functionally and visually optional
	 * (i.e., does not force user to interact with the element
	 * and removes red asterisk next to label).
	 */
	isOptional?: boolean;
}

/**
 * Define the general layout for a single input component with its form
 * label, input element, tooltip with helper text, and child elements
 * (if any). All input components in this file call this function and pass
 * in their own relevant `inputSection` elements for their respective inputs.
 * @param props All required parameters in `InputProps` (`name` & `label`) plus
 * any optional parameters
 * @param inputSection JSX element passed by specific component functions
 * (e.g., `TextInput`)
 * @returns Formatted input section in grid format
 */
export const Layout = <T extends FieldValues>(
	props: InputProps<T>,
	inputSection: JSX.Element
) => {
	/** Styling for MUI `Grid item` elements. */
	const gridStyle = {
		display: "flex",
		alignItems: "center",
		marginBlock: 0.5,
	} as const;
	return (
		<Grid container alignItems="center" justifyContent="center">
			{/** Container for label of `inputSection`. */}
			<Grid item xs={3} justifyContent="center" sx={gridStyle}>
				<FormLabel
					sx={{
						color: props.isChild ? "green" : textColor,
						".MuiFormLabel-asterisk": {
							color: "#00AEEF",
							"&$error": {
								color: "#00AEEF",
							},
						},
					}}
					required={!props.isOptional}
				>
					{props.label}
				</FormLabel>
			</Grid>
			{/** Input section passed by non-generic component functions. */}
			<Grid item xs={12} sm={8.99} justifyContent="center" sx={gridStyle}>
				{inputSection}
			</Grid>
			{/** Tooltip component ('?' symbol; shows helper text on hover) */}
			<Grid item xs={0.01} sx={gridStyle}>
				{props.helperText && (
					<Tooltip title={props.helperText}>
						<IconButton>
							<HelpOutlineIcon />
						</IconButton>
					</Tooltip>
				)}
			</Grid>
			{/**
			 * Allow for components to be placed within the same overall
			 * container.
			 */}
			<Grid item sx={gridStyle}>
				{props.children}
			</Grid>
		</Grid>
	);
};

interface MultiRangeSliderProps {
	min: number;
	max: number;
  }
  
export const MultiRangeSlider: FC<MultiRangeSliderProps> = ({ min, max }) => {
	const [minVal, setMinVal] = useState(min);
	const [maxVal, setMaxVal] = useState(max);
	const minValRef = useRef(min);
	const maxValRef = useRef(max);
	const range = useRef<HTMLDivElement>(null); 
  
	// Convert to percentage
	const getPercent = useCallback((value: number) =>
	  Math.round(((value - min) / (max - min)) * 100), [min, max])
  
	// Set width of the range to decrease from the left side
	useEffect(() => {
	  const minPercent = getPercent(minVal);
	  const maxPercent = getPercent(maxValRef.current);
  
	  if (range.current) {
		range.current.style.left = `${minPercent}%`;
		range.current.style.width = `${maxPercent - minPercent}%`;
	  }
	}, [minVal, getPercent]);
  
	// Set width of the range to decrease from the right side
	useEffect(() => {
	  const minPercent = getPercent(minValRef.current);
	  const maxPercent = getPercent(maxVal);
  
	  if (range.current) {
		range.current.style.width = `${maxPercent - minPercent}%`;
	  }
	}, [maxVal, getPercent]);
  
	return (
	  <div className="container">
		  <input
			type="range"
			min={min}
			max={max}
			value={minVal}
			onChange={(event: ChangeEvent<HTMLInputElement>) => { 
			  const value = Math.min(Number(event.target.value), maxVal - 1);
			  setMinVal(value);
			  minValRef.current = value;
			}}
			className="thumb thumb--left"
			// style={{ zIndex: minVal > max - 100 && "5" }}
		  />
		  <input
			type="range"
			min={min}
			max={max}
			value={maxVal}
			onChange={(event: ChangeEvent<HTMLInputElement>) => {  
			  const value = Math.max(Number(event.target.value), minVal + 1);
			  setMaxVal(value);
			  maxValRef.current = value;
			}}
			className="thumb thumb--right"
		  />
  
		  <div className="slider">
			<div className="slider__track"></div>
			<div ref={range} className="slider__range"></div>
			<div className="slider__left-value">{minVal}</div>
			<div className="slider__right-value">{maxVal}</div>
		  </div>
		</div>
	);
  };


/**
 * Define a component used to take text, text area (i.e., a larger text box),
 * or numerical input from the user. A single instance of this component is
 * tied to a single input value from a Zod schema. Inputs are validated to be
 * numerical according to the input's corresponding rules in this Zod schema.
 * @param props
 * Required:
 * 1) `name` and `label` from `InputProps`
 *
 * Optional:
 * 1) `isTextArea`: expands text input box to be a larger text area
 * 2) `isNumber`: converts user input to a number if possible
 * 3) Optional parameters from `InputProps`
 * @returns `TextInput` (JSX element)
 */
export const TextInput = <T extends FieldValues>(
	props: InputProps<T> & {
		isTextArea?: boolean;
		isNumber?: boolean;
	}
) => {
	const {
		formState: { errors },
		control,
	} = useFormContext<T>();
	return (
		<Controller
			name={props.name}
			control={control}
			render={({ field: { onChange, ...rest } }) => {
				return Layout(
					props,
					<TextField
						{...rest}
						error={get(errors, props.name) !== undefined}
						onChange={(e) => {
							const value = e.target.value;
							/**
							 * If the input box accepts numbers, check if
							 * the input provided by the user is a float;
							 * if it is, convert it to a number away from
							 * a string. If it isn't a float or if the input
							 * box doesn't accept numbers, leave the string
							 * as-is so that Zod validation can display errors
							 * to the user.
							 */
							if (
								props.isNumber &&
								value.trim() !== "" &&
								!isNaN(+value) &&
								!value.endsWith(".")
							) {
								// Convert to number
								onChange(+value);
							} else {
								// Leave as string
								onChange(value);
							}
						}}
						helperText={get(errors, props.name)?.message}
						InputProps={{
							sx: {
								borderRadius: 5,
								width: 300,
								boxShadow:
									"rgba(149, 157, 165, 0.2) 0px 8px 24px",
							},
						}}
						required={!props.isOptional}
						multiline={!!props.isTextArea}
						rows={5}
					/>
				);
			}}
		/>
	);
};

/**
 * Define a component used for inputs that take binary values
 * (e.g., `true`/`false`). A single instance of this component is tied
 * to a single input value from a Zod schema.
 * @param props All required parameters in `InputProps` (`name` & `label`) plus
 * any optional parameters
 * @returns `SwitchInput` (JSX element)
 */
export const SwitchInput = <T extends FieldValues>(props: InputProps<T>) => {
	const { control } = useFormContext<T>();
	return (
		<Controller
			name={props.name}
			control={control}
			render={({ field: { value, ...field } }) => {
				return Layout(
					props,
					<Switch
						{...field}
						checked={!!value}
						required={!props.isOptional}
					/>
				);
			}}
		/>
	);
};

/**
 * Define a component container representing a group of radio buttons
 * controlling the same input value from a Zod schema. This component
 * is ideal for when the user must select a single option from a small
 * list of pre-defined options.
 * @param props
 * Required:
 * 1) `valueLabelMap`: maps each radio button's actual value to
 * its user-visible label
 * 2) `name` & `label` from `InputProps
 *
 * Optional:
 * 1) Optional parameters from `InputProps`
 * @returns `RadioInput` (JSX element)
 */
export const RadioInput = <T extends FieldValues>(
	props: InputProps<T> & {
		valueLabelMap: { [key: string]: string };
	}
) => {
	const { control } = useFormContext<T>();
	return (
		<Controller
			name={props.name}
			control={control}
			render={({ field }) => {
				return Layout(
					props,
					<RadioGroup row {...field}>
						{Array.from(
							Object.keys(props.valueLabelMap),
							(radioValue) => {
								return (
									<FormControlLabel
										key={radioValue}
										value={radioValue}
										control={<Radio />}
										label={props.valueLabelMap[radioValue]}
									/>
								);
							}
						)}
					</RadioGroup>
				);
			}}
		/>
	);
};

/**
 * A component that combines the functionality of a dropdown list with
 * an autocomplete text input box corresponding to each of the options
 * in this list. This component only accepts inputs from this defined list
 * of options. The functionality of a single instance of this component
 * is tied to a single input value from a Zod schema.
 * @param props
 * Required:
 * 1) `options`: list of acceptable dropdown or text-autocomplete input values
 * 2) `name` & `label` from `InputProps`
 *
 * Optional:
 * 1) Optional parameters from `InputProps`
 * @returns `AutoOptionInput` (JSX element)
 */
export const AutoOptionInput = <T extends FieldValues>(
	props: InputProps<T> & {
		options: string[];
	}
) => {
	const {
		control,
		formState: { errors },
	} = useFormContext<T>();
	return (
		<Controller
			name={props.name}
			control={control}
			render={({ field }) => {
				return Layout(
					props,
					<Autocomplete
						{...field}
						options={props.options}
						onChange={(_e, data) => field.onChange(data)}
						renderInput={(params) => {
							return (
								<TextField
									{...params}
									error={
										get(errors, props.name) !== undefined
									}
									helperText={
										get(errors, props.name)?.message
									}
									required={!props.isOptional}
									InputProps={{
										...params.InputProps,
										sx: {
											borderRadius: 5,
											width: 300,
											boxShadow:
												"rgba(149, 157, 165, 0.2) 0px 8px 24px",
										},
									}}
								/>
							);
						}}
					/>
				);
			}}
		/>
	);
};

export const AutoOptionInputFreeSolo = <TForm extends FieldValues>(
    props: InputProps<TForm> & {
        options: string[];          // Allele Names (e.g., "HLA-A*01:03")
        optionsMap: Record<string, string>; // Allele Name -> Sequence mapping
    }
) => {
    const {
        control,
        formState: { errors },
        watch,
    } = useFormContext<TForm>();

    // Watch the form state to get the current sequence (the value stored in RHF)
    const mhcSequence = watch(props.name) as string | undefined;

    return (
        <Controller
            name={props.name}
            control={control}
            render={({ field }) => {
                
                // *** FIX: Display the full SEQUENCE in the input field. ***
                // The Autocomplete's 'value' prop is controlled by the raw RHF state (the sequence).
                // This ensures that after selection, the field shows the long sequence, 
                // and for free-text input, it shows the typed sequence.
                const displayValue = mhcSequence || null;

                // Custom onChange logic: runs when an option is selected or text input is submitted/blurred.
                const handleAutocompleteChange = (
                    _event: React.SyntheticEvent,
                    // data is the selected Allele Name (string) or the free-text input (string) or null
                    data: string | null 
                ) => {
                    let finalValue: string | null = null;
                    
                    if (data === null) {
                        finalValue = "";
                    } else if (props.optionsMap[data]) {
                        // Case 1: Known Allele Name selected -> Use the full Sequence from the map
                        finalValue = props.optionsMap[data];
                    } else {
                        // Case 2: Custom Sequence typed -> Use the typed string
                        finalValue = data;
                    }

                    // Update RHF state with the final Sequence value
                    field.onChange(finalValue);
                };

                // The logic that fires when the user types a custom sequence is now fully handled 
                // by the Autocomplete's internal mechanics combined with the field props in renderInput.
                // We remove onInputChange to simplify and prevent state conflicts.
                
                return Layout( // Use the user's Layout wrapper
                    props,
                    <Autocomplete
                        // Standard RHF binding (ref, onBlur)
                        {...field}
                        
                        // The 'value' prop is set to the full sequence/raw text
                        value={displayValue} 
                        
                        // The Autocomplete's options are the Allele Names
                        options={props.options}
                        
                        // Crucial for allowing custom sequence input
                        freeSolo
                        
                        // Primary handler: fires on selection or blur
                        onChange={handleAutocompleteChange}
                        
                        // This tells the Autocomplete what to render for each option in the dropdown list.
                        // It ensures the dropdown still shows the clear Allele Name keys.
                        getOptionLabel={(option) => option}

                        renderInput={(params) => {
                            const errorObject = get(errors, props.name);
                            const hasError = errorObject !== undefined;

                            return (
                                <TextField
                                    {...params}

                                    // Error/HelperText from RHF
                                    error={hasError}
                                    helperText={errorObject?.message}
                                    
                                    // Required status
                                    required={!props.isOptional}

                                    // Apply the specific styling from the original component
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: {
                                            borderRadius: 5,
                                            width: 300,
                                            boxShadow:
                                                "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                                        },
                                    }}
                                />
                            );
                        }}
                    />
                );
            }}
        />
    );
};

/**
 * Define a component used to take a single file or multiple files if
 * specified in the optional parameter `hasMultipleFiles`. The
 * functionality of a single instance of this component
 * is tied to a single input value from a Zod schema.
 * @param props
 * Required:
 * 1) `name` and `label` from `InputProps`
 *
 * Optional:
 * 1) `acceptedTypes`: list of accepted file types for user upload
 * 2) `allowMultipleFiles`: specifies whether to accept multiple files or not
 * 3) Optional parameters from `InputProps`
 * @returns `FileInput` (JSX element)
 */
export const FileInput = <T extends FieldValues>(
	props: InputProps<T> & {
		acceptedTypes?: string[];
		allowMultipleFiles?: boolean;
	}
) => {
	const {
		control,
		formState: { errors },
		getValues,
	} = useFormContext<T>();
	return (
		<Controller
			name={props.name}
			control={control}
			render={({ field }) => {
				return Layout(
					props,
					<>
						{/**
						 * Upload file(s) button; contains file input
						 * logic.
						 */}
						<Button
							component="label"
							variant="contained"
							startIcon={<CloudUploadIcon />}
							sx={{
								borderRadius: 5,
								textTransform: "none",
							}}
						>
							{props.allowMultipleFiles
								? "Upload file(s)"
								: "Upload file"}
							{/** Input component that accepts files. */}
							<TextField
								{...field}
								variant="standard"
								type="file"
								required={!props.isOptional}
								value={field.value?.fileName}
								inputProps={{
									accept: props.acceptedTypes?.join(", "),
									multiple: !!props.allowMultipleFiles,
								}}
								InputProps={{
									disableUnderline: true,
								}}
								onChange={(e) => {
									let files = (e.target as HTMLInputElement)
										.files;
									// Change only if at least 1 file exists.
									if (files) {
										/**
										 * Send a single file or multiple files
										 * to React Hook Form based on the
										 * `allowMultipleFiles` parameter.
										 */
										props.allowMultipleFiles
											? field.onChange(files)
											: field.onChange(files[0]);
									}
								}}
							/>
						</Button>
						{/** Display uploaded file(s) to the user. */}
						<FilesInfoTooltip
							value={getValues(props.name)}
							allowMultipleFiles={!!props.allowMultipleFiles}
						/>
						{/** Display most Zod-based errors. */}
						{get(errors, props.name) !== undefined && (
							<span style={{ color: "red" }}>
								{get(errors, props.name).message}
							</span>
						)}
						{/**
						 * If a file field is required, display text showing
						 * this info to the user if: 1) no other errors are
						 * displayed; 2) the user has not interacted with the
						 * field by uploading their own file. This is handled
						 * separately due to missing support in React Hook
						 * Form.
						 */}
						{!props.isOptional &&
							getValues(props.name) === dummyFile &&
							get(errors, props.name) === undefined && (
								<span style={{ color: "red" }}>
									File required.
								</span>
							)}
					</>
				);
			}}
		/>
	);
};

/**
 * Given any `value`, return a tooltip displaying the number
 * (always visible) and name(s) (when hovered over) of the file(s)
 * in it. This function is only relevant if `value` is a `File`
 * or `FileList`. Otherwise, the tooltip will simply display
 * 0 for the number of files uploaded (and the text "No file(s)
 * uploaded" when hovered over).
 * @param props
 * - `value`: input to read file names from
 * - `allowMultipleFiles`: used for displaying appropriate tooltip text
 * for a given `FileInput`
 * @returns `FilesInfoTooltip`
 */
const FilesInfoTooltip = (props: {
	value: any;
	allowMultipleFiles: boolean;
}) => {
	/** Default values when `value` is undefined. */
	let displayString = props.allowMultipleFiles
		? "No file(s) uploaded."
		: "No file uploaded.";
	let numFiles = 0;
	if (props.value instanceof FileList) {
		// Create an array from the FileList & map each file to its name.
		let fileNames = Array.from(props.value).map((file) => file.name);
		numFiles = fileNames.length;
		displayString = `Uploaded file(s): [${fileNames.join(", ")}]`;
	} else if (props.value instanceof File) {
		/**
		 * Ignore the dummyFile default value; this value indicates that the
		 * user has not interacted with the field.
		 */
		if (props.value !== dummyFile) {
			displayString = `Uploaded file: ${props.value.name}`;
			numFiles = 1;
		}
	}
	return (
		/** Display the relevant tooltip string. */
		<Tooltip title={displayString}>
			<Button
				component="label"
				variant="outlined"
				style={{
					borderRadius: 20,
					marginInline: 10,
				}}
			>
				{/**
				 * Display number of files uploaded to
				 * the user.
				 */}
				{numFiles}
			</Button>
		</Tooltip>
	);
};

/**
 * A button that is used to reset all form values to
 * their original default states before user modification
 * using React Hook Form's `useFormContext`.
 * @returns `ResetButton` (JSX element)
 */
export const ResetButton = () => {
	const { reset } = useFormContext();
	return (
		<Button
			variant="outlined"
			startIcon={<WarningIcon />}
			sx={{
				width: 120,
				height: 25,
				fontSize: 10,
				marginTop: 2,
				textTransform: "none",
				borderRadius: 20,
				color:"#0076B6",
				bgcolor: "#FFFFFF",
				'&:hover': {backgroundColor: "#00AEEF", }
			}}
			onClick={() => {
				reset();
			}}
		>
			Reset form
		</Button>
	);
};

/**
 * A button that is used to signal the submission of a form
 * to the `onSubmit` function defined in a form conforming to
 * the specifications of React Hook Form.
 * @param props
 * Required:
 * 1) `onSubmit`: function that defines form submit logic
 * @returns `SubmitButton` (JSX element)
 */
export const SubmitButton = <T extends FieldValues>(props: {
	onSubmit: SubmitHandler<T>;
}) => {
	const { handleSubmit } = useFormContext<T>();
	return (
		<Button
			onClick={handleSubmit(props.onSubmit)}
			variant="contained"
			startIcon={<CheckCircleOutlineIcon />}
			sx={{
				width: 300,
				height: 50,
				fontSize: 15,
				marginTop: 1,
				textTransform: "none",
				borderRadius: 20,
				color:"#FFFFFF",
				bgcolor: "#0076B6",
				'&:hover': {backgroundColor: "#00AEEF", }
			}}
		>
			Submit
		</Button>
	);
};

/**
 * A button that is used to signal the submission of a form
 * to the `onSubmit` function defined in a form conforming to
 * the specifications of React Hook Form.
 * @param props
 * Required:
 * 1) `onSubmit`: function that defines form submit logic
 * @returns `SearchButton` (JSX element)
 */
export const SearchButton = <T extends FieldValues>(props: {
	onSubmit: SubmitHandler<T>;
}) => {
	const { handleSubmit } = useFormContext<T>();
	return (
		<Button
			onClick={handleSubmit(props.onSubmit)}
			variant="contained"
			startIcon={<Search />}
			sx={{
				width: 300,
				height: 50,
				fontSize: 15,
				marginTop: 1,
				textTransform: "none",
				borderRadius: 20,
				color:"#FFFFFF",
				bgcolor: "#0076B6",
				'&:hover': {backgroundColor: "#00AEEF", }
			}}
		>
			Search
		</Button>
	);
};

/**
 * Define a navigation bar that is visible at the top of every page.
 * This navigation bar is formatted separately from other page elements
 * (in `Outlet`) in order to reduce unnecessary re-rendering, as it is
 * static.
 * @param props
 * Required:
 * 1) `title`: name of the website; displayed prominently as an HTML
 * `h4` heading
 * 2) `pageLinkMap`: maps other pages' user-visible names to their relative
 * paths (relative to the home page)
 * 3) `color`: background color of the navigation bar
 * @returns `Navbar` (JSX element)
 */
export const Navbar = (props: {
	title: string;
	pageLinkMap: { [key: string]: string };
	color: string;
}) => {
	/**
	 * Controls for opening and closing the page link menu ('☰') when the
	 * screen size is small.
	 */
	const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
	const closeMenu = () => setAnchor(null);
	const openMenu = (event: React.MouseEvent<HTMLElement>) =>
		setAnchor(event.currentTarget);
	return (
		<AppBar
			component="nav"
			sx={{ position: "static", backgroundColor: props.color }}
		>
			<Toolbar>
				{/** Title */}
				<Typography variant="h4" sx={{ flexGrow: 1 }}>
					<CustomLink
						path="/"
						className="link"
						title={props.title}
						newTab={false}
					/>
				</Typography>
				{/**
				 * Display when the screen is relatively large for the
				 * navbar. The title and page links are all aligned in
				 * the same row in this view. */}
				<Box sx={{ display: { xs: "none", md: "flex" } }}>
					{Array.from(Object.keys(props.pageLinkMap), (page) => {
						return (
							<CustomLink
								path={props.pageLinkMap[page]}
								key={page}
								className="link"
								title={page}
								style={{
									padding: 10,
									marginInline: 5,
								}}
								newTab={false}
							/>
						);
					})}
				</Box>
				{/**
				 * Display when the screen is small. This view consists
				 * of an icon ('☰') that displays all page links in a menu
				 * when clicked on.
				 */}
				<Box sx={{ display: { xs: "flex", md: "none" } }}>
					{/** Icon that triggers opening of the menu */}
					<IconButton
						size="large"
						edge="start"
						color="inherit"
						onClick={openMenu}
					>
						<MenuIcon />
					</IconButton>
					{/** Menu with all relevant page links */}
					<Menu
						anchorEl={anchor}
						open={Boolean(anchor)}
						onClose={closeMenu}
					>
						{Array.from(Object.keys(props.pageLinkMap), (page) => {
							return (
								<CustomLink
									path={props.pageLinkMap[page]}
									title={
										<MenuItem key={`${page}-button`}>
											{page}
										</MenuItem>
									}
									key={page}
									style={{
										textDecoration: "none",
										color: "black",
									}}
									newTab={false}
								/>
							);
						})}
					</Menu>
				</Box>
			</Toolbar>
		</AppBar>
	);
};

/**
 * Define a container for viewing PDB files using PDBe Molstar.
 * Parameters must be passed to this component in order to view
 * different types of results.
 * @param props
 * Required:
 * 1) `ligandUrl`: path to ligand file
 * 2) `receptorUrl`: path to receptor file
 * @returns `<div id="PDBViewer">` element (container for viewing PDB files)
 */
export const PDBViewer = (props: {
	ligandUrl: string;
	receptorUrl: string;
}) => {
	const pdbViewerStyle: React.CSSProperties = {
		width: "90%",
		height: "70%",
		position: "relative",
		margin: "auto",
		padding: "10px",
	};
	// Synchronize PDBViewer with `ligandUrl` and `receptorUrl` dependencies
	useEffect(() => {
		const script = document.createElement("script");
		script.type = "text/javascript";
		script.async = true;
		script.innerHTML = `
			if (typeof structures === 'undefined') {
				// Structure list
				const structures = [
					{
						customData: {
							url: '${props.ligandUrl}',
							format: 'pdb'
						}
					},
					{
						customData: {
							url: '${props.receptorUrl}',
							format: 'pdb'
						}
					}
				];

				// Set options (check available options list in the documentation)
				var options = {
					...structures[0]
				}

				var structIndex = 1;
				var viewerInstance = new PDBeMolstarPlugin();
				var viewerContainer = document.getElementById("PDBViewer");
				viewerInstance.render(viewerContainer, options);
				viewerInstance.events.loadComplete.subscribe(() => {
					if (structIndex === structures.length) return;
					viewerInstance.visual.update(structures[structIndex], false);
					structIndex++;
				});
			}
	  	`;

		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, [props.ligandUrl, props.receptorUrl]);
	return <div id="PDBViewer" style={pdbViewerStyle}></div>;
};

/**
 * Button used for links. Links will open in a new tab due to the
 * `target="_blank"` line.
 * @param props
 * Required:
 * 1) `link`: the link to which the user will be redirected
 * 2) `displayText`: text to display over the button
 * @returns `LinkButton`
 */
export const LinkButton = (props: { link: string; displayText: string }) => {
	return (
		<Button
			variant="contained"
			startIcon={<LinkIcon />}
			sx={{
				borderRadius: 20,
				marginInline: 0.5,
				textTransform: "none",
				color:"#FFFFFF",
				bgcolor: "#0076B6",
				'&:hover': {backgroundColor: "#00AEEF",},
			}}
			// Link to relevant page
			href={props.link}
			// Makes the link open in a new tab.
			target="_blank"
		>
			{props.displayText}
		</Button>
	);
};

/**
 * Define a container for referencing an academic publication.
 * @param props
 * Required:
 * 1) `title`: string rep. publication title
 *
 * Optional:
 * 1) `publisherLink`: string rep. LINK to publisher
 * 2) `pdfLink`: string rep. LINK to PDF of publication
 * @returns Container with reference information.
 */
export const Reference = (props: {
	title: string;
	publisherLink?: string;
	pdfLink?: string;
}) => {
	return (
		<div>
			{props.title}
			<div
				style={{
					display: "flex",
					marginBlock: "0.5rem",
					justifyContent: "center",
				}}
			>
				{props.pdfLink && (
					<LinkButton link={props.pdfLink} displayText="PDF" />
				)}
				{props.publisherLink && (
					<LinkButton
						link={props.publisherLink}
						displayText="Publisher"
					/>
				)}
			</div>
		</div>
	);
};

/**
 * Define a container for an image followed by a caption in bold and a
 * description explaining it further.
 * @param props
 * Required:
 * 1) `imgName`: the name of the image to display (e.g., `image.png`);
 * do not provide its path, as it is handled in this function
 * 2) `caption`: string representing caption for the image in bold
 * 3) `desc`: string explaining the image and caption further
 *
 * Optional:
 * 1) `marginTop`: controls the upper margin of the image itself (not
 * the container)
 * 2) `width`: controls the width of both the caption and the image
 * @returns `Figure`
 */
export const Figure = (props: {
	imgName: string;
	caption: string;
	desc: string;
	marginTop?: string;
	width?: string;
}) => {
	return (
		<figure
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
			}}
		>
			<img
				src={require(`./media/${props.imgName}`)}
				width={props.width ? props.width : "80%"}
				alt=""
				style={{ marginTop: props.marginTop }}
			/>
			<figcaption
				style={{
					width: props.width ? props.width : "80%",
					marginTop: "1rem",
				}}
			>
				<b>{`${props.caption} `}</b>
				{props.desc}
			</figcaption>
		</figure>
	);
};

/**
 * Use this component to create a "bubble" around a certain section
 * of text on the webpage. The input `width` may also be passed in order
 * to adjust the size of the background created by this component.
 * Lastly, the color of this background bubble can be changed in
 * `constants.tsx`.
 * @param props
 * May pass `width` (string) & `padding` (number) to adjust the size of
 * this bubble.
 */
export const TextBackground = styled(Paper)(
	(props: { width?: string; padding?: number }) => ({
		borderRadius: 20,
		padding: props.padding ? props.padding : 50,
		margin: 10,
		width: props.width ? props.width : "80%",
		backgroundColor: bubbleColor,
		elevation: 100,
		display: "flex",
		alignItems: "center",
		flexDirection: "column",
	})
);

/**
 * Define a component that is used as the footer of every page on the
 * DINC-Ensemble website.
 * @param props
 * @returns `Footer`
 */
export const Footer = (props: { backgroundColor: string }) => {
	const marginInline = 10;
	return (
		<Box
			component="footer"
			sx={{
				padding: 2,
				backgroundColor: props.backgroundColor,
			}}
		>
			<Container
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<Container
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						margin: "0.5rem",
					}}
				>
					<CustomLink
						className="link"
						path="https://kavrakilab.org/"
						title={
							<div>
								Kavraki Lab,
								<LaunchIcon fontSize="small" />
							</div>
						}
						style={{
							textDecoration: "underline",
							marginInline: marginInline,
						}}
						newTab
					/>
					<CustomLink
						className="link"
						path="https://cs.rice.edu/"
						title={
							<div>
								Computer Science Department,
								<LaunchIcon fontSize="small" />
							</div>
						}
						style={{
							textDecoration: "underline",
							marginInline: marginInline,
						}}
						newTab
					/>
					<CustomLink
						className="link"
						path="https://www.rice.edu/"
						title={
							<div>
								Rice University
								<LaunchIcon fontSize="small" />
							</div>
						}
						style={{
							textDecoration: "underline",
							marginInline: marginInline,
						}}
						newTab
					/>
				</Container>
				<Typography variant="body2" color="white">
					{`Copyright © Rice University ${new Date().getFullYear()}.`}
				</Typography>
			</Container>
		</Box>
	);
};

/**
 * Customized variant of `HashLink` from React Router Hash Link.
 * @param props
 * Required:
 * 1) `title`: text to display over the link
 * 2) `path`: link (or relative path) to redirect the user to
 * 3) `newTab`: set to true to direct the user to a new tab; false if not
 *
 * Optional:
 * 1) `className`: identify the CSS class of the text
 * 2) `download`: set to true to download the data at the link; leave
 * undefined or false otherwise
 * 3) `style`: an object customizing the style of the link
 * @returns
 */
export const CustomLink = (props: {
	title: React.ReactNode;
	path: string;
	newTab: boolean;
	className?: string;
	download?: boolean;
	style?: React.CSSProperties;
}) => {
	return (
		<HashLink
			to={props.path}
			className={props.className}
			target={props.newTab ? "_blank" : ""}
			style={props.style}
			download={props.download}
		>
			{props.title}
		</HashLink>
	);
};

/**
 * Set global styles through this theme. Make sure to wrap your React app
 * with `<ThemeProvider theme={theme}>` in `index.tsx` and also include
 * `<CssBaseline /> within this wrapper element.
 */
export const theme = createTheme({
	typography: {
		fontFamily: fontFamily,
	},
	palette: {
		background: {
			default: pageBackgroundColor,
		},
		text: {
			primary: textColor,
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: cssStyleOverrides,
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					width: 250,
				},
			},
		},
	},
});

/**
 * Prevent the enter key from being used to submit the form.
 * @param e An event representing the user clicking on some key
 */
export const disableEnterKey = (e: React.KeyboardEvent) => {
	if (e.key === "Enter") {
		e.preventDefault();
	}
};

const ScatterCategoryElement = (props: {
	category_name: string;
	category_data: Array<Result>;
}) => {
	const elem = {
		name: props.category_name,
		type: "scatter",
		emphasis: {
			focus: "series",
		},
		data: props.category_data,
		markArea: {
			silent: true,
			itemStyle: {
				color: "transparent",
				borderWidth: 1,
				borderType: "dashed",
			},
			data: [
				[
					{
						xAxis: "min",
						yAxis: "min",
					},
					{
						xAxis: "max",
						yAxis: "max",
					},
				],
			],
		},
		markLine: {
			lineStyle: {
				type: "solid",
			},
			data: [{ type: "average", name: "AVG" }, { xAxis: 160 }],
		},
	};
	return elem;
};

const ScatterPlotOption = (props: {
	categories: Array<string>;
	scatter_elements: Array<any>;
}) => {
	return {
		title: {
			text: "Energy/RMSD scatterplot of the docked result",
			subtext: "Data from your docking experiment",
		},
		grid: {
			left: "3%",
			right: "7%",
			bottom: "7%",
			containLabel: true,
		},
		tooltip: {
			// trigger: 'axis',
			showDelay: 0,
			formatter: function (params: any) {
				if (params.value.length > 1) {
					return (
						params.seriesName +
						" :<br/>" +
						params.value[0] +
						" Å " +
						params.value[1] +
						"kcal/mol"
					);
				} else {
					return (
						params.seriesName +
						" :<br/>" +
						params.name +
						" : " +
						params.value +
						"kcal/mol "
					);
				}
			},
			axisPointer: {
				show: true,
				type: "cross",
				lineStyle: {
					type: "dashed",
					width: 1,
				},
			},
		},
		toolbox: {
			feature: {
				dataZoom: {},
				brush: {
					type: ["rect", "polygon", "clear"],
				},
			},
		},
		brush: {},
		legend: {
			data: props.categories,
			left: "center",
			type: "scroll",
			bottom: 0,
		},
		xAxis: [
			{
				type: "value",
				scale: true,
				title: "RMSD (Å)",
				splitLine: {
					show: false,
				},
			},
		],
		yAxis: [
			{
				type: "value",
				scale: true,
				axisLabel: {
					formatter: "{value} kcal/mol",
				},
				splitLine: {
					show: false,
				},
			},
		],
		series: props.scatter_elements,
	};
};
